import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { ownDevicesQuerySchema } from "./device-read";

const id = ownDevicesQuerySchema.shape.cursor.unwrap();
const deviceName = z
  .string()
  .refine(
    (value) => Array.from(value).length <= 255,
    "Device name exceeds 255 characters",
  );
const token = z
  .string()
  .min(1)
  .refine(
    (value) => new TextEncoder().encode(value).length <= 65535,
    "Token exceeds storage limit",
  );
export const deviceRegistrationSchema = z.strictObject({
  deviceId: id,
  deviceName,
  platform: z.enum(["ios", "android"]),
  token,
});
export const deviceUpdateSchema = z
  .strictObject({
    token: token.optional(),
    deviceName: deviceName.optional(),
    status: z.enum(["active", "unsubscribed"]).optional(),
    lastSeenAt: z.iso.datetime({ offset: true, local: true }).optional(),
  })
  .refine(
    (value) => Object.values(value).some((value) => value !== undefined),
    "An edit is required",
  );
export type DeviceRegistration = z.output<typeof deviceRegistrationSchema>;
export type DeviceUpdate = z.output<typeof deviceUpdateSchema>;
export const deviceWriteResultSchema = z.object({ id });
const write = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  NOT_FOUND: { message: "Not Found" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  SERVICE_UNAVAILABLE: { message: "Authentication unavailable" },
});
export const deviceWriteContract = {
  registerDevice: write
    .input(
      z.object({
        query: z.strictObject({}).default({}),
        body: deviceRegistrationSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/me/devices",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(deviceWriteResultSchema),
  updateDevice: write
    .input(
      z.object({
        params: z.strictObject({ deviceId: id }),
        query: z.strictObject({}).default({}),
        body: deviceUpdateSchema,
      }),
    )
    .meta(
      openapi({
        method: "PATCH",
        path: "/api/v2/me/devices/{deviceId}",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(deviceWriteResultSchema),
};
